import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  Paper,
  Link,
  Stack,
  Tabs,
  Tab,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CodeIcon from '@mui/icons-material/Code';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ArchiveIcon from '@mui/icons-material/Archive';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import MusicNoteIcon from '@mui/icons-material/MusicNote';

import ReactPlayer from 'react-player';
import { getFileTypeCategory } from '../utils/fileUtils';

const PreviewContainer = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  overflow: 'visible',
}));

const FileImage = styled('img')(({ theme }) => ({
  maxWidth: '100%',
  maxHeight: '400px',
  display: 'block',
  margin: '0 auto',
  borderRadius: theme.shape.borderRadius,
}));

// const UrlTextField = styled(TextField)(({ theme }) => ({
//   marginTop: theme.spacing(2),
//   marginBottom: theme.spacing(2),
//   width: '100%',
// }));

const CodePreview = styled(Box)(({ theme }) => ({
  fontFamily: 'monospace',
  whiteSpace: 'pre-wrap',
  overflowX: 'auto',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5',
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  maxHeight: '400px',
  overflowY: 'auto',
}));

const AudioPlayer = styled('audio')(({ theme }) => ({
  width: '100%',
  borderRadius: theme.shape.borderRadius,
  margin: '1rem 0',
}));

const PreviewCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  textAlign: 'center',
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
}));

const FilePreview = ({ fileUrl, fileMetadata, originalFilename, hashFilename, ipfsGatewayUrl }) => {
  // const [copied, setCopied] = useState(false); // For future use: copy fileUrl to clipboard
  const [gatewayUrlCopied, setGatewayUrlCopied] = useState(false);
  const [fileContent, setFileContent] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [fileTypeCategory, setFileTypeCategory] = useState('binary');
  const [isLoading, setIsLoading] = useState(false);

  // Check if file is available for preview
  const hasFile = Boolean(fileUrl);
  
  // Determine file type category when metadata changes
  useEffect(() => {
    if (fileMetadata && fileMetadata.type && fileMetadata.name) {
      const category = getFileTypeCategory(fileMetadata.type, fileMetadata.name);
      setFileTypeCategory(category);
      console.log('File type category:', category);
      
      // Pre-fetch text content for text and code files
      if ((category === 'text' || category === 'code') && ipfsGatewayUrl) {
        fetchTextContent(ipfsGatewayUrl);
      }
    }
  }, [fileMetadata, ipfsGatewayUrl]);
  
  // Fetch text content for code and text files
  const fetchTextContent = async (url) => {
    setIsLoading(true);
    setFetchError(null);
    
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch content: ${response.status} ${response.statusText}`);
      }
      
      const textContent = await response.text();
      setFileContent(textContent);
    } catch (error) {
      console.error('Error fetching file content:', error);
      setFetchError(`Failed to load content: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Handle copy to clipboard (for future use)
  // const handleCopy = () => {
  //   navigator.clipboard.writeText(fileUrl);
  //   setCopied(true);
  //   setTimeout(() => setCopied(false), 2000);
  // };

  // Handle copy blob URL to clipboard
  const handleCopyGatewayUrl = () => {
    navigator.clipboard.writeText(ipfsGatewayUrl);
    setGatewayUrlCopied(true);
    setTimeout(() => setGatewayUrlCopied(false), 2000);
  };
  
  // Helper function for downloading files
  const downloadFile = async (filename) => {
    try {
      // Use the ipfsGatewayUrl to ensure we're downloading from the blob storage location
      const downloadUrl = ipfsGatewayUrl;
      console.log(`Starting download from Blob Storage: ${downloadUrl} as ${filename}`);

      // Fetch the file from blob storage directly
      const response = await fetch(downloadUrl);

      if (!response.ok) {
        throw new Error(`Failed to fetch from Blob Storage: ${response.status} ${response.statusText}`);
      }
      
      // Get the file content as a blob
      const blob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      
      // Trigger the download
      link.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        console.log(`Download completed for: ${filename}`);
      }, 1000);
      
    } catch (error) {
      console.error('Error downloading file from Blob Storage:', error);
      alert(`Download failed: ${error.message}.\n\nPlease try manually downloading by:\n1. Right-clicking the File URL above\n2. Selecting "Save Link As..."`);
    }
  };

  // Handle download original file
  const handleDownload = () => {
    downloadFile(originalFilename || fileMetadata?.name || 'download');
  };

  // Handle download with hash filename
  const handleDownloadWithHash = () => {
    downloadFile(hashFilename || 'download');
  };
  
  // If no file, don't render anything
  if (!hasFile) {
    return null;
  }

  return (
    <PreviewContainer elevation={3}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          File Preview
        </Typography>
        
        {/* File URL with SHA-256 Hash */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom color="text.secondary">
            File URL (SHA-256 Hash)
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            p: 2, 
            border: '1px solid', 
            borderColor: 'primary.main', 
            borderRadius: 1,
            bgcolor: 'background.paper',
            wordBreak: 'break-all'
          }}>
            <Typography variant="body2" sx={{ flexGrow: 1, fontFamily: 'monospace' }}>
              {ipfsGatewayUrl || "Loading File URL..."}
            </Typography>
            <Tooltip title={gatewayUrlCopied ? "Copied!" : "Copy to clipboard"}>
              <IconButton onClick={handleCopyGatewayUrl} size="small" sx={{ ml: 1 }}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        {/* Preview based on file type category */}
        <Box sx={{ my: 3 }}>
          <Typography variant="h6" gutterBottom>
            File Preview
          </Typography>
          
          {/* Display view options for file types with multiple preview modes */}
          {(fileTypeCategory === 'code' || fileTypeCategory === 'text') && (
            <Tabs 
              value={currentTab} 
              onChange={handleTabChange} 
              sx={{ mb: 2 }}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Preview" />
              <Tab label="Raw" />
              <Tab label="Download" />
            </Tabs>
          )}
          
          {/* IMAGE PREVIEW */}
          {fileTypeCategory === 'image' && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Image Preview
              </Typography>
              <FileImage 
                src={ipfsGatewayUrl} 
                alt="Uploaded file preview" 
                loading="lazy" 
                crossOrigin="anonymous"
                onError={(e) => {
                  console.error("Image load error", e);
                  e.target.onerror = null;
                  // Use a placeholder image if loading fails
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2YwZjBmMCIgLz4KICA8dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjI0IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+SW1hZ2UgUHJldmlldyBVbmF2YWlsYWJsZTwvdGV4dD4KICA8dGV4dCB4PSI1MCUiIHk9IjYwJSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE2IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+VXBsb2FkIHN1Y2Nlc3NmdWwuIFVzZSBkb3dubG9hZCBidXR0b25zIGJlbG93LjwvdGV4dD4KPC9zdmc+';
                }} 
              />
              <Typography variant="caption" color="info.main" sx={{ display: 'block', mt: 1 }}>
                Note: If the image doesn't appear, try downloading using the buttons below.
              </Typography>
            </Box>
          )}
          
          {/* VIDEO PREVIEW */}
          {fileTypeCategory === 'video' && (
            <Box sx={{ aspectRatio: '16/9', maxWidth: '100%', margin: '0 auto' }}>
              <ReactPlayer
                url={ipfsGatewayUrl}
                controls
                width="100%"
                height="100%"
                config={{
                  file: {
                    attributes: { controlsList: 'nodownload' },
                  },
                }}
              />
              <Typography variant="caption" color="info.main" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                Note: If the video doesn't play, you can download it using the buttons below.
              </Typography>
            </Box>
          )}
          
          {/* AUDIO PREVIEW */}
          {fileTypeCategory === 'audio' && (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <MusicNoteIcon sx={{ fontSize: 64, color: 'primary.main' }} />
              </Box>
              <Typography variant="subtitle1" gutterBottom>
                Audio File
              </Typography>
              <AudioPlayer 
                controls 
                src={ipfsGatewayUrl}
                controlsList="nodownload"
              >
                Your browser does not support the audio element.
              </AudioPlayer>
              <Typography variant="caption" color="info.main" sx={{ display: 'block', mt: 1 }}>
                Note: If audio doesn't play, try downloading using the buttons below.
              </Typography>
            </Box>
          )}
          
          {/* PDF PREVIEW */}
          {fileTypeCategory === 'pdf' && (
            <Box sx={{ textAlign: 'center', my: 3 }}>
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<OpenInNewIcon />}
                href={ipfsGatewayUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ py: 1.5, px: 3 }}
              >
                Open PDF Document
              </Button>
              <Typography variant="caption" color="info.main" sx={{ display: 'block', mt: 2 }}>
                PDFs open in a new tab for viewing. If it doesn't load, you can download it directly.
              </Typography>
            </Box>
          )}
          
          {/* TEXT FILES */}
          {fileTypeCategory === 'text' && (
            <>
              {currentTab === 0 && (
                <Box>
                  {isLoading ? (
                    <Box sx={{ textAlign: 'center', my: 2, p: 3 }}>
                      <Typography>Loading text content...</Typography>
                    </Box>
                  ) : fetchError ? (
                    <Alert severity="warning" sx={{ my: 2 }}>
                      {fetchError}
                    </Alert>
                  ) : fileContent ? (
                    <CodePreview>
                      {fileContent.length > 10000 
                        ? fileContent.substring(0, 10000) + '...\n\n[Content truncated for preview. Download the file for the complete content.]' 
                        : fileContent}
                    </CodePreview>
                  ) : (
                    <PreviewCard>
                      <TextSnippetIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography>
                        Text content preview not available. Try downloading the file.
                      </Typography>
                    </PreviewCard>
                  )}
                </Box>
              )}
              
              {currentTab === 1 && (
                <Box sx={{ textAlign: 'center', my: 2 }}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<OpenInNewIcon />}
                    href={ipfsGatewayUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Raw Text
                  </Button>
                </Box>
              )}
              
              {currentTab === 2 && (
                <Box sx={{ textAlign: 'center', my: 2 }}>
                  <Stack direction="row" spacing={2} justifyContent="center">
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<DownloadIcon />}
                      onClick={() => downloadFile(originalFilename)}
                    >
                      Download Text File
                    </Button>
                  </Stack>
                </Box>
              )}
            </>
          )}
          
          {/* CODE FILES */}
          {fileTypeCategory === 'code' && (
            <>
              {currentTab === 0 && (
                <Box>
                  {isLoading ? (
                    <Box sx={{ textAlign: 'center', my: 2, p: 3 }}>
                      <Typography>Loading code content...</Typography>
                    </Box>
                  ) : fetchError ? (
                    <Alert severity="warning" sx={{ my: 2 }}>
                      {fetchError}
                    </Alert>
                  ) : fileContent ? (
                    <CodePreview>
                      {fileContent.length > 10000 
                        ? fileContent.substring(0, 10000) + '...\n\n[Content truncated for preview. Download the file for the complete code.]' 
                        : fileContent}
                    </CodePreview>
                  ) : (
                    <PreviewCard>
                      <CodeIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography>
                        Code preview not available. Try downloading the file.
                      </Typography>
                    </PreviewCard>
                  )}
                </Box>
              )}
              
              {currentTab === 1 && (
                <Box sx={{ textAlign: 'center', my: 2 }}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<OpenInNewIcon />}
                    href={ipfsGatewayUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Raw Code
                  </Button>
                </Box>
              )}
              
              {currentTab === 2 && (
                <Box sx={{ textAlign: 'center', my: 2 }}>
                  <Stack direction="row" spacing={2} justifyContent="center">
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<DownloadIcon />}
                      onClick={() => downloadFile(originalFilename)}
                    >
                      Download Code File
                    </Button>
                  </Stack>
                </Box>
              )}
            </>
          )}
          
          {/* DOCUMENT FILES */}
          {fileTypeCategory === 'document' && (
            <Box sx={{ textAlign: 'center', my: 3 }}>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <InsertDriveFileIcon sx={{ fontSize: 64, color: 'primary.main' }} />
              </Box>
              <Typography variant="subtitle1" gutterBottom>
                Document File
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<OpenInNewIcon />}
                href={ipfsGatewayUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ mt: 2 }}
              >
                View Document
              </Button>
              <Typography variant="caption" color="info.main" sx={{ display: 'block', mt: 2 }}>
                Note: Browser support for viewing documents varies. If the document doesn't open correctly, 
                you can download it using the buttons below.
              </Typography>
            </Box>
          )}
          
          {/* 3D MODEL FILES */}
          {fileTypeCategory === 'model' && (
            <Box sx={{ textAlign: 'center', my: 3 }}>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <ModelTrainingIcon sx={{ fontSize: 64, color: 'primary.main' }} />
              </Box>
              <Typography variant="subtitle1" gutterBottom>
                3D Model File
              </Typography>
              <Typography variant="body2" paragraph>
                3D model preview is not available in the browser. Download the file to view in a 3D viewer application.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={() => downloadFile(originalFilename)}
                sx={{ mt: 1 }}
              >
                Download 3D Model
              </Button>
            </Box>
          )}
          
          {/* ARCHIVE FILES */}
          {fileTypeCategory === 'archive' && (
            <Box sx={{ textAlign: 'center', my: 3 }}>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <ArchiveIcon sx={{ fontSize: 64, color: 'primary.main' }} />
              </Box>
              <Typography variant="subtitle1" gutterBottom>
                Archive File
              </Typography>
              <Typography variant="body2" paragraph>
                This is a compressed archive file. Download to extract its contents.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={() => downloadFile(originalFilename)}
                sx={{ mt: 1 }}
              >
                Download Archive
              </Button>
            </Box>
          )}
          
          {/* BINARY FILES */}
          {(fileTypeCategory === 'binary' && 
            !['image', 'video', 'audio', 'pdf', 'text', 'code', 'document', 'model', 'archive'].includes(fileTypeCategory)) && (
            <Box sx={{ textAlign: 'center', my: 3 }}>
              <PreviewCard>
                <Typography variant="subtitle1" gutterBottom>
                  File Type: {fileMetadata?.type || "Unknown"}
                </Typography>
                <Typography variant="body2" paragraph>
                  Preview not available for this file type. 
                  The file has been successfully uploaded to IPFS and can be downloaded below.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  onClick={() => downloadFile(originalFilename)}
                  sx={{ mt: 1 }}
                >
                  Download File
                </Button>
              </PreviewCard>
            </Box>
          )}
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        {/* File Metadata */}
        {fileMetadata && (
          <Box>
            <Typography variant="h6" gutterBottom>
              File Metadata
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Name" 
                  secondary={fileMetadata.name} 
                  primaryTypographyProps={{ fontWeight: 'bold' }} 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Type" 
                  secondary={fileMetadata.type} 
                  primaryTypographyProps={{ fontWeight: 'bold' }} 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Size" 
                  secondary={fileMetadata.size} 
                  primaryTypographyProps={{ fontWeight: 'bold' }} 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Last Modified" 
                  secondary={fileMetadata.lastModified} 
                  primaryTypographyProps={{ fontWeight: 'bold' }} 
                />
              </ListItem>
            </List>
          </Box>
        )}
        
        <Box sx={{ mt: 4, p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
          <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', fontWeight: 'bold' }}>
            Download Options
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Original filename:</strong> {originalFilename}
            </Typography>
            <Typography variant="body1">
              <strong>IPFS hash filename:</strong> {hashFilename}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              sx={{ minWidth: '220px', py: 1 }}
            >
              Download Original
            </Button>
            
            <Button
              variant="contained"
              color="secondary"
              size="large"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadWithHash}
              sx={{ minWidth: '220px', py: 1 }}
            >
              Download with Hash
            </Button>
          </Box>
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        {/* Security Notice */}
        <Alert severity="warning" variant="outlined" sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Security Notice
          </Typography>
          <Typography variant="body2">
            Vercel Blob Storage stores files with public URLs that are accessible to anyone with the link.
            Do not upload sensitive or private information. For confidential files, use
            <Link
              href="https://upload-encrypted.freeweb3.com/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ mx: 1 }}
            >
              encrypted upload
            </Link>
            instead.
          </Typography>
        </Alert>
      </CardContent>
    </PreviewContainer>
  );
};

export default FilePreview;